import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Truck, Recycle, Clock, Shield, ThumbsUp, Star, ArrowRight, CheckCircle2 } from 'lucide-react';

export default function HomePage() {
  return (
    <div className="min-h-screen">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1574974671999-24b7dfbb0d53?auto=format&fit=crop&w=2000&q=80"
            alt="Professional waste management"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-hero"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-accent text-accent-foreground border-0">
              #1 Rated Dumpster Rental Service
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
              Reliable Dumpster Rentals for Every Project
            </h1>
            <p className="text-lg sm:text-xl text-background/90 mb-8 leading-relaxed max-w-2xl">
              Fast delivery, flexible rental periods, and competitive pricing. From home cleanouts to major construction projects, we've got you covered.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-elegant text-base">
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-background/10 backdrop-blur-sm border-background text-background hover:bg-background/20 text-base">
                <Link to="/services">View Services</Link>
              </Button>
            </div>
            <div className="flex items-center space-x-6 mt-8">
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-background" />
                <span className="text-sm text-background/90">Same-Day Delivery</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="w-5 h-5 text-background" />
                <span className="text-sm text-background/90">No Hidden Fees</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-muted">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">15+</div>
              <div className="text-sm text-muted-foreground">Years in Business</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">10,000+</div>
              <div className="text-sm text-muted-foreground">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">500+</div>
              <div className="text-sm text-muted-foreground">Dumpsters Available</div>
            </div>
            <div className="text-center">
              <div className="text-3xl sm:text-4xl font-bold text-primary mb-2">50+</div>
              <div className="text-sm text-muted-foreground">Cities Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Why Choose Us
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We make waste management simple, reliable, and affordable
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-border hover:shadow-lg transition-smooth group">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-gradient-primary transition-smooth">
                  <Truck className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-smooth" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Fast Delivery</h3>
                <p className="text-sm text-muted-foreground">Same-day or next-day delivery available for most orders</p>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-lg transition-smooth group">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-gradient-primary transition-smooth">
                  <Recycle className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-smooth" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Eco-Friendly</h3>
                <p className="text-sm text-muted-foreground">Committed to responsible waste disposal and recycling</p>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-lg transition-smooth group">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-gradient-primary transition-smooth">
                  <Clock className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-smooth" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Flexible Rental</h3>
                <p className="text-sm text-muted-foreground">Rent for as long as you need with no hidden fees</p>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-lg transition-smooth group">
              <CardContent className="pt-6">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-gradient-primary transition-smooth">
                  <Shield className="w-6 h-6 text-primary group-hover:text-primary-foreground transition-smooth" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Fully Insured</h3>
                <p className="text-sm text-muted-foreground">Complete protection and peace of mind for every rental</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-gradient-subtle">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Our Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Professional dumpster rental solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-border hover:shadow-elegant transition-smooth flex flex-col">
              <CardContent className="pt-6 flex flex-col h-full">
                <Badge className="mb-4 w-fit bg-secondary text-secondary-foreground">
                  Popular Choice
                </Badge>
                <h3 className="text-xl font-semibold text-foreground mb-3">Residential Rentals</h3>
                <p className="text-sm text-muted-foreground mb-4 flex-grow">Perfect for home renovations, spring cleaning, and moving projects</p>
                <div className="mb-4">
                  <div className="text-xs text-muted-foreground mb-2">Available Sizes:</div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">10 Yard</Badge>
                    <Badge variant="outline" className="text-xs">15 Yard</Badge>
                    <Badge variant="outline" className="text-xs">20 Yard</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                  <span className="text-lg font-bold text-primary">Starting at $299</span>
                  <Button asChild variant="link" className="text-primary hover:text-primary-light group">
                    <Link to="/services" className="flex items-center">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-smooth" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-elegant transition-smooth flex flex-col">
              <CardContent className="pt-6 flex flex-col h-full">
                <Badge className="mb-4 w-fit bg-secondary text-secondary-foreground">
                  Popular Choice
                </Badge>
                <h3 className="text-xl font-semibold text-foreground mb-3">Commercial Services</h3>
                <p className="text-sm text-muted-foreground mb-4 flex-grow">Reliable waste management solutions for businesses of all sizes</p>
                <div className="mb-4">
                  <div className="text-xs text-muted-foreground mb-2">Available Sizes:</div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">20 Yard</Badge>
                    <Badge variant="outline" className="text-xs">30 Yard</Badge>
                    <Badge variant="outline" className="text-xs">40 Yard</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                  <span className="text-lg font-bold text-primary">Starting at $449</span>
                  <Button asChild variant="link" className="text-primary hover:text-primary-light group">
                    <Link to="/services" className="flex items-center">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-smooth" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-elegant transition-smooth flex flex-col">
              <CardContent className="pt-6 flex flex-col h-full">
                <Badge className="mb-4 w-fit bg-secondary text-secondary-foreground">
                  Popular Choice
                </Badge>
                <h3 className="text-xl font-semibold text-foreground mb-3">Construction Debris</h3>
                <p className="text-sm text-muted-foreground mb-4 flex-grow">Heavy-duty dumpsters for construction and demolition projects</p>
                <div className="mb-4">
                  <div className="text-xs text-muted-foreground mb-2">Available Sizes:</div>
                  <div className="flex flex-wrap gap-2">
                    <Badge variant="outline" className="text-xs">30 Yard</Badge>
                    <Badge variant="outline" className="text-xs">40 Yard</Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                  <span className="text-lg font-bold text-primary">Starting at $549</span>
                  <Button asChild variant="link" className="text-primary hover:text-primary-light group">
                    <Link to="/services" className="flex items-center">
                      Learn More
                      <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-smooth" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              What Our Customers Say
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-border hover:shadow-lg transition-smooth">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                </div>
                <p className="text-sm text-muted-foreground mb-4 italic">"Excellent service! They delivered on time and picked up exactly when promised. Made our home renovation so much easier."</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ThumbsUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-sm">Sarah Johnson</div>
                    <div className="text-xs text-muted-foreground">Homeowner</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-lg transition-smooth">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                </div>
                <p className="text-sm text-muted-foreground mb-4 italic">"We use them for all our construction projects. Professional, reliable, and always competitive pricing. Highly recommend!"</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ThumbsUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-sm">Mike Peters</div>
                    <div className="text-xs text-muted-foreground">Contractor</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border hover:shadow-lg transition-smooth">
              <CardContent className="pt-6">
                <div className="flex mb-4">
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                  <Star className="w-5 h-5 fill-accent text-accent" />
                </div>
                <p className="text-sm text-muted-foreground mb-4 italic">"Their commercial waste services are top-notch. Regular pickups, clean service, and great customer support."</p>
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <ThumbsUp className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold text-foreground text-sm">Emily Chen</div>
                    <div className="text-xs text-muted-foreground">Business Owner</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-primary relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-lg text-primary-foreground/90 mb-8">
              Get a free quote today and see why thousands of customers trust us with their waste management needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-accent hover:bg-accent/90 text-accent-foreground shadow-elegant text-base">
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="bg-primary-foreground/10 backdrop-blur-sm border-primary-foreground text-primary-foreground hover:bg-primary-foreground/20 text-base">
                <a href="tel:1-800-DUMPSTER">Call Now</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
